import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { t } from "../i18n";
import { formatClock } from "../lib/format";

/**
 * Голосовое в переписке: кнопка, «волна» с прогрессом, время. Своё <audio> на пузырь с
 * preload="metadata" — длина видна сразу, а сам звук грузится по нажатию. Звучит одно
 * голосовое за раз: новое ставит на паузу предыдущее (и общий плеер сайта — onPlay).
 *
 * Перемотка — настоящий <input type="range> поверх волны: с клавиатуры и VoiceOver она
 * работает как любой ползунок.
 */

let current: HTMLAudioElement | null = null;

const BARS = 32;

/** Высоты столбиков «волны» от ключа: у одного голосового — всегда одинаковые. */
export function waveform(seed: string, bars: number = BARS): number[] {
  let hash = 2166136261;
  for (const char of seed) hash = Math.imul(hash ^ (char.codePointAt(0) ?? 0), 16777619) >>> 0;
  const out: number[] = [];
  for (let i = 0; i < bars; i += 1) {
    hash = Math.imul(hash ^ (hash >>> 13), 0x5bd1e995) >>> 0;
    const base = 0.28 + ((hash % 1000) / 1000) * 0.72;
    // Края тише середины — как у настоящей фразы
    const envelope = 0.55 + 0.45 * Math.sin((Math.PI * (i + 0.5)) / bars);
    out.push(Math.round(base * envelope * 100) / 100);
  }
  return out;
}

export interface VoicePlayerProps {
  url: string | null;
  seed: string;
  /** Длина, если известна заранее (своя запись); иначе — из метаданных файла. */
  durationMs?: number | null;
  mine: boolean;
  /** Перед началом звука: остановить остальной звук сайта. */
  onPlay?: () => void;
  onMediaError?: () => void;
}

export function VoicePlayer({ url, seed, durationMs = null, mine, onPlay, onMediaError }: VoicePlayerProps) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(durationMs ? durationMs / 1000 : 0);
  const bars = useMemo(() => waveform(seed), [seed]);
  const sliderId = useId();
  // Запись MediaRecorder в Chrome (webm) приходит без длины: duration = Infinity, пока
  // файл не прочитан до конца. Прыжок в самый конец заставляет браузер её посчитать
  const probing = useRef(false);

  useEffect(() => {
    const element = audio.current;
    return () => {
      if (element && current === element) current = null;
      element?.pause();
    };
  }, []);

  const total = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const progress = total > 0 ? Math.min(1, time / total) : 0;

  function toggle() {
    const element = audio.current;
    if (!element || !url) return;
    if (!element.paused) {
      element.pause();
      return;
    }
    if (current && current !== element) current.pause();
    current = element;
    onPlay?.();
    setLoading(true);
    element.play().catch(() => {
      setLoading(false);
      setFailed(true);
    });
  }

  return (
    <div className={`voice${mine ? " voice--mine" : ""}${failed ? " voice--failed" : ""}`}>
      <button
        type="button"
        className="voice__button"
        onClick={toggle}
        disabled={!url || failed}
        aria-label={playing ? t("messenger.voice.pause") : t("messenger.voice.play")}
      >
        {loading && !playing ? <span className="voice__spinner" aria-hidden="true" /> : <Icon name={playing ? "pause" : "play"} size={20} />}
      </button>
      <div className="voice__track">
        <div className="voice__bars" aria-hidden="true">
          {bars.map((height, index) => (
            <i
              key={index}
              className={index / bars.length < progress ? "voice__bar voice__bar--played" : "voice__bar"}
              style={{ height: `${Math.round(height * 100)}%` }}
            />
          ))}
        </div>
        <label className="sr-only" htmlFor={sliderId}>
          {t("messenger.voice.seek")}
        </label>
        <input
          id={sliderId}
          className="voice__seek"
          type="range"
          min={0}
          max={total || 1}
          step={0.1}
          value={Math.min(time, total || 1)}
          disabled={!url || failed || total === 0}
          aria-valuetext={`${formatClock(time)} / ${formatClock(total)}`}
          onChange={(event) => {
            const element = audio.current;
            const value = Number(event.target.value);
            if (element && Number.isFinite(value)) {
              element.currentTime = value;
              setTime(value);
            }
          }}
        />
      </div>
      <span className="voice__time">{failed ? "—" : formatClock(playing || time > 0 ? time : total)}</span>
      {url && (
        <audio
          ref={audio}
          src={url}
          preload="metadata"
          onLoadedMetadata={(event) => {
            const element = event.currentTarget;
            const value = element.duration;
            if (Number.isFinite(value) && value > 0) setDuration(value);
            else if (value === Number.POSITIVE_INFINITY && !probing.current) {
              probing.current = true;
              element.currentTime = 1e7;
            }
          }}
          onDurationChange={(event) => {
            const element = event.currentTarget;
            const value = element.duration;
            if (!Number.isFinite(value) || value <= 0) return;
            setDuration(value);
            if (probing.current) {
              probing.current = false;
              element.currentTime = 0;
            }
          }}
          onTimeUpdate={(event) => {
            if (!probing.current) setTime(event.currentTarget.currentTime);
          }}
          onPlaying={() => {
            setPlaying(true);
            setLoading(false);
          }}
          onPause={() => setPlaying(false)}
          onEnded={(event) => {
            setPlaying(false);
            setTime(0);
            event.currentTarget.currentTime = 0;
            if (current === event.currentTarget) current = null;
          }}
          onError={() => {
            setFailed(true);
            setLoading(false);
            setPlaying(false);
            onMediaError?.();
          }}
        />
      )}
      {failed && <span className="sr-only">{t("messenger.voice.failedToLoad")}</span>}
    </div>
  );
}
